import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ErrorPage, NotFoundPage } from './components/ErrorPage';
import LoginPage from './routes/login';
// import SignalsPage from './routes/index';
import PlaysPage from './routes/plays';
import PlayDetailPage from './routes/play-detail';
import TradersPage from './routes/traders';
import TraderDetailPage from './routes/trader-detail';
import AlertsPage from './routes/alerts';
import LeaderboardPage from './routes/leaderboard';
import DebugPage from './routes/debug';
import SettingsPage from './routes/settings';
import UsersListPage from './routes/admin-users';
import UserDetailPage from './routes/admin-user-detail';
import { initSentry, wrapCreateBrowserRouter } from './lib/sentry';
import './styles.css';

initSentry();

const sentryCreateBrowserRouter = wrapCreateBrowserRouter(createBrowserRouter);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 30, // 30 seconds
      retry: 1,
    },
  },
});

const router = sentryCreateBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
    children: [
      { path: '/', element: <PlaysPage key="plays" /> },
      { path: '/plays/:id', element: <PlayDetailPage key="play-detail" /> },
      { path: '/traders', element: <TradersPage key="traders" /> },
      { path: '/traders/:traderId', element: <TraderDetailPage key="trader-detail" /> },
      { path: '/alerts', element: <AlertsPage key="alerts" /> },
      { path: '/leaderboard', element: <LeaderboardPage key="leaderboard" /> },
      // { path: '/signals', element: <SignalsPage key="signals" /> },
      {
        path: '/debug',
        element: (
          <ProtectedRoute requireAdmin>
            <DebugPage key="debug" />
          </ProtectedRoute>
        ),
      },
      { path: '/settings', element: <SettingsPage key="settings" /> },
      {
        path: '/settings/users',
        element: (
          <ProtectedRoute requireAdmin>
            <UsersListPage key="settings-users" />
          </ProtectedRoute>
        ),
      },
      {
        path: '/settings/users/:id',
        element: (
          <ProtectedRoute requireAdmin>
            <UserDetailPage key="settings-user-detail" />
          </ProtectedRoute>
        ),
      },
    ],
  },
  // Catch-all for 404
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);

createRoot(document.getElementById('root')!).render(
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);
